import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { CHARTER_FLIGHT_DETAILS } from 'constants/charterFlight'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import { rewriteCharterFeederInclusions } from 'lib/charterFlights/charterBundleOfferPageUtils'

const CHARTER_ROUTE_INFO = CHARTER_FLIGHT_DETAILS['melbourne-maldives']

interface Options {
  flightOrigin?: App.Airport
  hasFeederFlightIncluded: boolean
}

/**
 * Returns package inclusions with the selected feeder city reflected in a
 * charter flight inclusion, while preserving the standard inclusion groups.
 */
export default function usePackageUpgradeInclusions(pkg: App.Package, options: Options) {
  const { flightOrigin, hasFeederFlightIncluded } = options
  const intl = useIntl()
  const {
    inclusions: sourceInclusions,
    bonusInclusions,
    tacticalInclusions,
    luxPlusInclusions,
  } = useOfferInclusions(pkg)
  const inclusions = useMemo(() => {
    if (!hasFeederFlightIncluded || !flightOrigin) {
      return sourceInclusions
    }

    const { originAirport, destinationAirport } = CHARTER_ROUTE_INFO
    return rewriteCharterFeederInclusions(sourceInclusions, {
      feederCity: flightOrigin.name,
      originCity: originAirport.cityDesc,
      destinationCity: destinationAirport.cityDesc,
      intl,
    })
  }, [flightOrigin, hasFeederFlightIncluded, intl, sourceInclusions])

  return useMemo(() => ({
    inclusions,
    bonusInclusions,
    tacticalInclusions,
    luxPlusInclusions,
  }), [bonusInclusions, inclusions, luxPlusInclusions, tacticalInclusions])
}
